<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watchEffect } from "vue";
import StarIcon from "./Star.vue";

interface Props {
  modelValue?: number;
  ratingHover?: number;
  numberOfStars?: number;
  starColor?: string;
  inactiveColor?: string;
  starSize?: number;
  disableClick?: boolean;
  customSvg?: typeof StarIcon;
}

interface Emit {
  (e: "update:modelValue", v: number): void;
  (e: "update:ratingHover", v: number): void;
}

const props = withDefaults(defineProps<Props>(), {
  numberOfStars: 5,
  starSize: 10,
  modelValue: 0,
  ratingHover: 0,
  starColor: "#F7B32F",
  inactiveColor: "#e6e8ec",
  customSvg: StarIcon,
});

const emit = defineEmits<Emit>();

const componentToDisplay = computed(() => {
  return props.customSvg || StarIcon;
});

const utils = {
  rounded(value: number, decimalPlaces: number) {
    const power = Math.pow(10, decimalPlaces);
    return Math.round(value * power) / power;
  },
};

const starsContainer = ref<HTMLDivElement>();

// Return 0 when modelValue is not provided.
const rating = computed({
  get() {
    return props.modelValue ?? 0;
  },
  set(newVal) {
    let value = newVal;
    if (isNaN(value)) {
      value = 0;
    }
    emit("update:modelValue", value);
  },
});

// Return 0 when ratingHover is not provided.
const hoverRatingCount = computed({
  get() {
    return props.ratingHover ?? 0;
  },
  set(newVal) {
    let value = newVal;
    if (isNaN(value)) {
      value = 0;
    }
    emit("update:ratingHover", value);
  },
});

/**
 * Utility function to extract the pageX coordinate from either a MouseEvent or a TouchEvent.
 */
function getPageX(e: MouseEvent | TouchEvent): number {
  if ("touches" in e && e.touches.length > 0) {
    return e.touches[0].pageX;
  }
  return (e as MouseEvent).pageX;
}

/**
 * When the user interacts (click or touch), calculate the rating based on the position.
 */
function adjustRating(this: HTMLDivElement, e: MouseEvent | TouchEvent) {
  if (props.disableClick) return;
  const rect = this.getBoundingClientRect();
  const pageX = getPageX(e);

  // Calculate relative distance from the right edge.
  const relativeX = Math.abs(pageX - rect.right);
  const offsetWidth = rect.width;
  const numberOfStars = props.numberOfStars;

  let result = (relativeX / offsetWidth) * numberOfStars;

  // Round to the nearest 0.5 increment.
  result = Math.round(result * 2) / 2;

  // Clamp the result to the maximum.
  if (result > numberOfStars) {
    result = numberOfStars;
  }
  const roundedVal = utils.rounded(result, 1);

  rating.value = roundedVal;
}

/**
 * Calculate the preview rating similarly on hover or touch.
 */
function hoverRating(this: HTMLDivElement, e: MouseEvent | TouchEvent) {
  if (props.disableClick) return;
  const rect = this.getBoundingClientRect();
  const pageX = getPageX(e);

  const relativeX = Math.abs(pageX - rect.right);
  const offsetWidth = rect.width;
  const numberOfStars = props.numberOfStars;

  let result = (relativeX / offsetWidth) * numberOfStars;
  result = Math.round(result * 2) / 2;
  if (result > numberOfStars) {
    result = numberOfStars;
  }
  const roundedVal = utils.rounded(result, 1);

  hoverRatingCount.value = roundedVal;
}

/**
 * Reset the hover rating preview on mouse out or when a touch ends/cancels.
 */
function resetRating() {
  hoverRatingCount.value = 0;
}

/**
 * Compute the percentage used for the inner styling.
 */
const percent = computed(() => {
  // Use hover rating if available; otherwise, use confirmed rating.
  const activeRating = hoverRatingCount.value || rating.value;
  const normalizedRating = Math.min(Math.max(activeRating, 0), props.numberOfStars);
  return (normalizedRating / props.numberOfStars) * 100;
});

// Update the CSS variables on the container.
watchEffect(() => {
  const styleValues = {
    "--vue3StarRatingsInnerColor": props.inactiveColor,
    "--vue3StarRatingsOuterColor": props.starColor,
    "--vue3StarRatingOuterWidth": `${percent.value}%`,
    "--vue3StarRatingIconSize": `${props.starSize}px`,
  };

  for (const [key, value] of Object.entries(styleValues)) {
    starsContainer.value?.style.setProperty(key, value);
  }
});

/**
 * Dedicated handler for the touchend event.
 */
function touchEndHandler(e: TouchEvent) {
  adjustRating.call(starsContainer.value!, e);
  resetRating();
}

onMounted(() => {
  // Mouse events
  starsContainer.value?.addEventListener("click", adjustRating);
  starsContainer.value?.addEventListener("mouseover", hoverRating);
  starsContainer.value?.addEventListener("mouseout", resetRating);

  // Touch events
  starsContainer.value?.addEventListener("touchstart", hoverRating);
  starsContainer.value?.addEventListener("touchmove", hoverRating);
  starsContainer.value?.addEventListener("touchend", adjustRating);
  starsContainer.value?.addEventListener("touchcancel", resetRating);
});

onBeforeUnmount(() => {
  starsContainer.value?.removeEventListener("click", adjustRating);
  starsContainer.value?.removeEventListener("mouseover", hoverRating);
  starsContainer.value?.removeEventListener("mouseout", resetRating);

  starsContainer.value?.removeEventListener("touchstart", hoverRating);
  starsContainer.value?.removeEventListener("touchmove", hoverRating);
  starsContainer.value?.removeEventListener("touchend", adjustRating);
  starsContainer.value?.removeEventListener("touchcancel", resetRating);
});
</script>

<template>
  <div
    class="vue3-star-ratings"
    ref="starsContainer"
    :style="{ pointerEvents: disableClick ? 'none' : 'auto' }"
  >
    <div class="vue3-star-ratings__outer">
      <component
        :is="componentToDisplay"
        v-for="i in numberOfStars"
        :key="i"
        class="vue3-star-ratings__icon"
      />
    </div>
    <div class="vue3-star-ratings__inner">
      <component
        :is="componentToDisplay"
        v-for="i in numberOfStars"
        :key="i"
        class="vue3-star-ratings__icon"
      />
    </div>
  </div>
</template>

<style scoped>
.vue3-star-ratings {
  width: fit-content;
  overflow: hidden;
  position: relative;
  white-space: nowrap;
}

.vue3-star-ratings * {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  line-height: 1;
}

.vue3-star-ratings__inner,
.vue3-star-ratings__outer {
  height: inherit;
}

.vue3-star-ratings__outer {
  position: absolute;
  top: 0;
  right: 0;
  width: var(--vue3StarRatingOuterWidth);
  max-width: 100%;
  overflow: hidden;
  color: var(--vue3StarRatingsOuterColor);
  transition: width 320ms cubic-bezier(0.075, 0.82, 0.165, 1);
}

.vue3-star-ratings__inner {
  color: var(--vue3StarRatingsInnerColor);
}

.vue3-star-ratings__icon {
  fill: currentColor;
  width: 20px;
  height: 20px;
  aspect-ratio: 1;
  cursor: pointer;
  display: inline-block;
  margin: 0px 2.5px;
}


@media(max-width: 576px) {
   .addRateModal .vue3-star-ratings__icon,
   .editRateModal .vue3-star-ratings__icon {
    width: 50px !important;
    height: 50px !important;
    margin: 0px 5.5px;
  }
} 

</style>
